import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';

type VariantBarcodesDiscardDialogProps = {
    isOpen: boolean;
    onKeepEditing: () => void;
    onDiscard: () => void;
};

/**
 * Asked when the barcodes drawer is closed with unsaved edits (rulebook
 * §5.5), in the shape of the import's `RowDrawerDiscardDialog`: Keep editing
 * is the safe answer, and dismissing the question (Escape, the backdrop, its
 * close button) is Keep editing too.
 */
export const VariantBarcodesDiscardDialog: React.FC<
    VariantBarcodesDiscardDialogProps
> = ({ isOpen, onKeepEditing, onDiscard }) => {
    const { t } = useTranslation('pages');

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && onKeepEditing()}
        >
            <Modal.Backdrop>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp('variant-barcodes-discard')}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t('products.detail.barcodes.discard.title')}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body>
                            <Typography color="muted" type="body-sm">
                                {t('products.detail.barcodes.discard.body')}
                            </Typography>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                onPress={onKeepEditing}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'variant-barcodes-discard-keep',
                                )}
                            >
                                {t('products.detail.barcodes.discard.keep')}
                            </Button>
                            <Button
                                onPress={onDiscard}
                                variant="danger"
                                {...getNodeTestIdProp(
                                    'variant-barcodes-discard-confirm',
                                )}
                            >
                                {t('products.detail.barcodes.discard.discard')}
                            </Button>
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
